import { flushSync } from "react-dom";

const DURATION_MS = 1100;

// Keep the outgoing screen above the real destination until it has mounted.
// Both screens are present during the dissolve: there is no intermediate color
// screen, no fade to the app background, and no dependence on View Transitions.
export function transitionRegistrationRoute(source, navigate, readySelector) {
  if (!source || window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
    flushSync(navigate);
    return Promise.resolve();
  }

  const rect = source.getBoundingClientRect();
  const clone = source.cloneNode(true);
  const originals = [source, ...source.querySelectorAll("*")];
  const copies = [clone, ...clone.querySelectorAll("*")];
  originals.forEach((original, index) => {
    const copy = copies[index];
    const styles = window.getComputedStyle(original);
    // Auth's ancestor classes disappear on unmount; freeze the rendered layout
    // so the photo, fonts and controls don't jump inside the outgoing layer.
    for (let i = 0; i < styles.length; i += 1) {
      const property = styles[i];
      copy.style.setProperty(property, styles.getPropertyValue(property));
    }
    copy.style.setProperty("animation", "none", "important");
    copy.style.setProperty("transition", "none", "important");
    copy.removeAttribute("id");
    if (original instanceof HTMLInputElement) copy.value = original.value;
  });
  Object.assign(clone.style, {
    position: "absolute", top: `${rect.top}px`, left: `${rect.left}px`,
    right: "auto", bottom: "auto", width: `${rect.width}px`,
    height: `${rect.height}px`, margin: "0", transform: "none",
  });

  const layer = document.createElement("div");
  layer.className = "vg-registration-transition-snapshot";
  layer.setAttribute("aria-hidden", "true");
  layer.setAttribute("inert", "");
  layer.appendChild(clone);
  document.body.appendChild(layer);

  return new Promise((resolve, reject) => {
    let observer;
    let deadline;
    let animation;
    let started = false;
    const cleanup = () => {
      observer?.disconnect();
      window.clearTimeout(deadline);
      layer.remove();
    };
    const finish = () => {
      cleanup();
      resolve();
    };
    const reveal = () => {
      if (started) return;
      const destination = document.querySelector(readySelector);
      if (!destination || layer.contains(destination)) return;
      started = true;
      observer?.disconnect();
      // Only the full-screen artwork gates the reveal. Lazy event thumbnails
      // must not hold up navigation while they are outside the viewport.
      const images = [...destination.querySelectorAll(".registration-journey-card__art")];
      Promise.all(images.map((img) => img.decode?.().catch(() => {}))).then(() => {
        if (!layer.isConnected) return;
        window.requestAnimationFrame(() => {
          if (!layer.isConnected) return;
          if (typeof layer.animate !== "function") {
            finish();
            return;
          }
          animation = layer.animate([
            { opacity: 1, filter: "blur(0px)", transform: "scale(1)" },
            { opacity: 0.82, filter: "blur(2px)", transform: "scale(1.008)", offset: 0.25 },
            { opacity: 0, filter: "blur(10px)", transform: "scale(1.035)" },
          ], {
            duration: DURATION_MS, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "forwards",
          });
          animation.finished.then(finish, finish);
        });
      });
    };
    // A failed route or stalled image must never leave an unclickable screen.
    deadline = window.setTimeout(() => { animation?.cancel(); finish(); }, 5000);
    observer = new MutationObserver(reveal);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-registration-destination"] });
    try {
      flushSync(navigate);
      reveal();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}
